import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User } from 'lucide-react';
import BenevolCheckLogo from '../components/BenevolCheckLogo';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AssociationDashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader, updateUser } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showProfile, setShowProfile] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [newAddress, setNewAddress] = useState(user?.association_address || '');
  const [savingAddress, setSavingAddress] = useState(false);

  useEffect(() => {
    fetchCertificates();
  }, []);

  useEffect(() => {
    setNewAddress(user?.association_address || '');
  }, [user]);

  const handleSaveAddress = async () => {
    setSavingAddress(true);
    try {
      const response = await axios.put(
        `${API}/auth/me`,
        { association_address: newAddress },
        { headers: getAuthHeader() }
      );
      if (updateUser) {
        updateUser(response.data);
      }
      setEditingAddress(false);
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
    } finally {
      setSavingAddress(false);
    }
  };

  const fetchCertificates = async () => {
    try {
      const response = await axios.get(`${API}/certificates`, {
        headers: getAuthHeader()
      });
      setCertificates(response.data);
    } catch (error) {
      console.error('Error fetching certificates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Statistiques
  const totalCertificates = certificates.length;
  const pendingCertificates = certificates.filter(c => c.status === 'pending_validation');
  const validatedCertificates = certificates.filter(c => c.status === 'validated');
  const totalHours = certificates.reduce((sum, c) => sum + c.hours_volume, 0);

  // Filtrage
  const filteredCertificates = certificates.filter(cert =>
    cert.volunteer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cert.mission_description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPending = filteredCertificates.filter(c => c.status === 'pending_validation');
  const filteredValidated = filteredCertificates.filter(c => c.status === 'validated');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <BenevolCheckLogo size="sm" showText={false} />
              <div className="flex-1">
                <h1 className="text-base sm:text-xl font-bold text-indigo-900">Bénévol'Check</h1>
                <p className="text-xs sm:text-sm text-gray-600 truncate">{user?.association_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowProfile(!showProfile)}
                data-testid="profile-button"
                className="flex-1 sm:flex-initial"
              >
                👤 {user?.name}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate('/profile')} title="Mon profil complet">
                <User size={18} />
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-button" className="flex-shrink-0">
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Profile Panel */}
      {showProfile && (
        <div className="bg-white border-b shadow-sm">
          <div className="container mx-auto px-4 py-4">
            <Card className="border-indigo-200 bg-indigo-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  👤 Profil du responsable
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Nom</p>
                    <p className="font-semibold text-gray-900" data-testid="profile-name">{user?.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Email</p>
                    <p className="font-semibold text-gray-900" data-testid="profile-email">{user?.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Association</p>
                    <p className="font-semibold text-indigo-700" data-testid="profile-association">{user?.association_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Ville (lieu de signature)</p>
                    {editingAddress ? (
                      <div className="flex gap-2 mt-1">
                        <Input
                          value={newAddress}
                          onChange={(e) => setNewAddress(e.target.value)}
                          placeholder="Entrez la ville..."
                          className="h-8 text-sm"
                          data-testid="address-input"
                        />
                        <Button 
                          size="sm" 
                          onClick={handleSaveAddress}
                          disabled={savingAddress}
                          data-testid="save-address-button"
                        >
                          {savingAddress ? '...' : '✓'}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setEditingAddress(false);
                            setNewAddress(user?.association_address || '');
                          }}
                        >
                          ✕
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-indigo-700" data-testid="profile-address">
                          {user?.association_address || 'Non renseignée'}
                        </p>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => setEditingAddress(true)}
                          className="h-6 px-2 text-xs"
                          data-testid="edit-address-button"
                        >
                          ✏️
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="pt-2 border-t border-indigo-200">
                  <p className="text-xs text-gray-500">
                    Rôle : <span className="font-medium text-indigo-600">Administrateur d'association</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      <main className="container mx-auto px-4 py-4 sm:py-8">
        {/* Title and Create Button */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
          <div className="flex-1">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-indigo-900 mb-2" data-testid="dashboard-title">
              Tableau de bord
            </h2>
            <p className="text-sm sm:text-base text-gray-600">
              Vue d'ensemble des attestations
            </p>
          </div>
          <Button 
            onClick={() => navigate('/create-certificate')} 
            size="lg"
            data-testid="create-certificate-button"
            className="w-full sm:w-auto"
          >
            📝 Créer une attestation
          </Button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <Card data-testid="stat-total">
            <CardHeader className="pb-2 sm:pb-3">
              <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">
                Total
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl sm:text-3xl font-bold text-indigo-900">{totalCertificates}</div>
            </CardContent>
          </Card>

          <Card data-testid="stat-pending" className="border-yellow-200 bg-yellow-50">
            <CardHeader className="pb-2 sm:pb-3">
              <CardTitle className="text-xs sm:text-sm font-medium text-yellow-800">
                En attente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl sm:text-3xl font-bold text-yellow-600">{pendingCertificates.length}</div>
            </CardContent>
          </Card>

          <Card data-testid="stat-validated" className="border-green-200 bg-green-50">
            <CardHeader className="pb-2 sm:pb-3">
              <CardTitle className="text-xs sm:text-sm font-medium text-green-800">
                Validées
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl sm:text-3xl font-bold text-green-600">{validatedCertificates.length}</div>
            </CardContent>
          </Card>

          <Card data-testid="stat-hours" className="border-blue-200 bg-blue-50">
            <CardHeader className="pb-2 sm:pb-3">
              <CardTitle className="text-xs sm:text-sm font-medium text-blue-800">
                Heures
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl sm:text-3xl font-bold text-blue-600">{totalHours}h</div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="mb-6">
          <Input
            type="text"
            placeholder="🔍 Rechercher par nom de bénévole ou mission..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
            data-testid="search-input"
          />
        </div>

        {/* Pending Attestations */}
        {filteredPending.length > 0 && (
          <Card className="mb-6 sm:mb-8" data-testid="pending-section">
            <CardHeader>
              <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
                ⏳ En attente
                <Badge className="bg-yellow-500">{filteredPending.length}</Badge>
              </CardTitle>
              <CardDescription className="text-sm">
                Attestations nécessitant validation
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Mobile View - Cards */}
              <div className="block lg:hidden space-y-4">
                {filteredPending.map((cert) => (
                  <Card key={cert.id} className="border-yellow-200" data-testid={`pending-card-${cert.id}`}>
                    <CardContent className="p-4 space-y-3">
                      <div>
                        <p className="font-semibold text-base">{cert.volunteer_name}</p>
                        <p className="text-sm text-gray-600">{cert.volunteer_email}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Mission</p>
                        <p className="text-sm">{cert.mission_description}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-gray-600">Période</p>
                          <p className="font-medium">
                            {new Date(cert.start_date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} - {new Date(cert.end_date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Volume</p>
                          <p className="font-semibold text-indigo-600">{cert.hours_volume}h</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => navigate(`/certificate/${cert.id}`)}
                        className="w-full"
                        data-testid={`view-pending-${cert.id}`}
                      >
                        Voir et valider
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {/* Desktop View - Table */}
              <div className="hidden lg:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bénévole</TableHead>
                      <TableHead>Mission</TableHead>
                      <TableHead>Période</TableHead>
                      <TableHead>Volume horaire</TableHead>
                      <TableHead>Créé le</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPending.map((cert) => (
                      <TableRow key={cert.id} data-testid={`pending-row-${cert.id}`}>
                        <TableCell className="font-medium">
                          <div>
                            <p className="font-semibold">{cert.volunteer_name}</p>
                            <p className="text-sm text-gray-600">{cert.volunteer_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{cert.mission_description}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          <div className="text-sm">
                            <p>{new Date(cert.start_date).toLocaleDateString('fr-FR')}</p>
                            <p className="text-gray-600">au</p>
                            <p>{new Date(cert.end_date).toLocaleDateString('fr-FR')}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-semibold text-indigo-600">
                            {cert.hours_volume} heures
                          </span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {new Date(cert.created_at).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            onClick={() => navigate(`/certificate/${cert.id}`)}
                            data-testid={`view-pending-${cert.id}`}
                          >
                            Voir et valider
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Validated Attestations */}
        <Card data-testid="validated-section">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
              ✅ Validées
              <Badge className="bg-green-500">{filteredValidated.length}</Badge>
            </CardTitle>
            <CardDescription className="text-sm">
              Historique complet des attestations
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredValidated.length === 0 ? (
              <div className="text-center py-8 text-sm sm:text-base text-gray-500">
                Aucune attestation validée
              </div>
            ) : (
              <>
                {/* Mobile View - Cards */}
                <div className="block lg:hidden space-y-4">
                  {filteredValidated.map((cert) => (
                    <Card key={cert.id} className="border-green-200" data-testid={`validated-card-${cert.id}`}>
                      <CardContent className="p-4 space-y-3">
                        <div>
                          <p className="font-semibold text-base">{cert.volunteer_name}</p>
                          <p className="text-sm text-gray-600">{cert.volunteer_email}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Mission</p>
                          <p className="text-sm">{cert.mission_description}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-gray-600">Période</p>
                            <p className="font-medium">
                              {new Date(cert.start_date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} - {new Date(cert.end_date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Volume</p>
                            <p className="font-semibold text-green-600">{cert.hours_volume}h</p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/certificate/${cert.id}`)}
                          className="w-full"
                          data-testid={`view-validated-${cert.id}`}
                        >
                          Consulter
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Desktop View - Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Bénévole</TableHead>
                        <TableHead>Mission</TableHead>
                        <TableHead>Période</TableHead>
                        <TableHead>Volume horaire</TableHead>
                        <TableHead>Validé le</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredValidated.map((cert) => (
                        <TableRow key={cert.id} data-testid={`validated-row-${cert.id}`}>
                          <TableCell className="font-medium">
                            <div>
                              <p className="font-semibold">{cert.volunteer_name}</p>
                              <p className="text-sm text-gray-600">{cert.volunteer_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>{cert.mission_description}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            <div className="text-sm">
                              <p>{new Date(cert.start_date).toLocaleDateString('fr-FR')}</p>
                              <p className="text-gray-600">au</p>
                              <p>{new Date(cert.end_date).toLocaleDateString('fr-FR')}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="font-semibold text-green-600">
                              {cert.hours_volume} heures
                            </span>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {cert.validation_date ? new Date(cert.validation_date).toLocaleDateString('fr-FR') : '-'}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/certificate/${cert.id}`)}
                              data-testid={`view-validated-${cert.id}`}
                            >
                              Consulter
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AssociationDashboardPage;
